import { useEffect, useState } from "react";

/**
 * An image thumbnail that degrades gracefully: a missing/404 src (e.g. a catalogue collection icon
 * that was never materialised to storage) shows a clean neutral placeholder instead of the browser's
 * broken-image glyph, so the picker stays presentable. Null src → placeholder too.
 */
export function Thumb({ src, alt = "", aspect = "4 / 3", fit = "cover", radius = 6, label }: {
  src: string | null | undefined;
  alt?: string;
  aspect?: string;
  fit?: "cover" | "contain";
  radius?: number;
  /** Optional short text shown in the placeholder (e.g. the option name's first word). */
  label?: string;
}) {
  const [broken, setBroken] = useState(false);
  // Reset when the src changes so a working image after a broken one still renders.
  useEffect(() => { setBroken(false); }, [src]);

  if (!src || broken) {
    return (
      <div
        aria-hidden="true"
        style={{
          width: "100%", aspectRatio: aspect, borderRadius: radius, background: "var(--bg)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "var(--muted-2)", overflow: "hidden",
        }}
      >
        {label ? (
          <span style={{ fontSize: 11, fontWeight: 600, padding: "0 6px", textAlign: "center", lineHeight: 1.2 }}>{label}</span>
        ) : (
          <DoorGlyph />
        )}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      decoding="async"
      onError={() => setBroken(true)}
      style={{
        width: "100%", aspectRatio: aspect, objectFit: fit,
        background: fit === "contain" ? "var(--bg)" : "transparent",
        borderRadius: radius, display: "block",
      }}
    />
  );
}

/** Neutral garage-door outline for the empty/broken placeholder. */
function DoorGlyph() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden>
      <rect x="3" y="4" width="18" height="17" rx="1.5" />
      <path d="M3 9h18M8 9v12M12 9v12M16 9v12" opacity="0.6" />
    </svg>
  );
}
